import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner, Image, Card, Container, Row, Col } from 'react-bootstrap';
import { FaUpload, FaImages, FaCheck, FaTimes } from 'react-icons/fa';
import { handleTokenAndPostRequest } from '../utils/utils';
import { computeBranding } from '../utils/useAgentDetails';

const ImageUploadButton = ({ agentDetails, onUploadSuccess, onDeleteSuccess, onShowSignInModal, propertyId, analysisLoading, uploadedImageUrls = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [uploadedImages, setUploadedImages] = useState(uploadedImageUrls);
  const [localToServerMap, setLocalToServerMap] = useState({});

  useEffect(() => {
    setUploadedImages(uploadedImageUrls);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const modalBody = document.querySelector('.modal-body');
      if (modalBody) {
        const viewportHeight = window.innerHeight;
        modalBody.style.maxHeight = `${viewportHeight - 100}px`;
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleButtonClick = () => {
    const jwt_token = localStorage.getItem('jwt_token');
    const urlParams = new URLSearchParams(window.location.search);
    const paramTokenFromUrl = urlParams.get('token');
    if (paramTokenFromUrl) {
      localStorage.setItem('param_token', paramTokenFromUrl);
    }
    const storedParamToken = localStorage.getItem('param_token');

    if (!jwt_token && !storedParamToken) {
      onShowSignInModal();
      return;
    }

    setShowModal(true);
  };
  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*';
  
    const removeInput = () => {
      if (input.parentNode) {
        document.body.removeChild(input);
      }
    };
  
    input.onchange = async (event) => {
      try {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
          // Display images immediately
          const fileUrls = files.map(file => URL.createObjectURL(file));
          setUploadedImages((prevImages) => [...prevImages, ...fileUrls]);
  
          // Map local URLs to null initially
          const newMap = {};
          fileUrls.forEach(url => {
            newMap[url] = null;
          });
          setLocalToServerMap((prevMap) => ({ ...prevMap, ...newMap }));
  
          // Upload and process images in the background
          handleUploadFiles(files, fileUrls);
        }
      } catch (error) {
        console.error('Error during file selection or upload:', error);
      } finally {
        removeInput(); // Ensure input is removed after use
      }
    };
  
    document.body.appendChild(input);
    input.click();
  
    // Use a timeout to ensure the input is removed after the dialog is closed
    setTimeout(removeInput, 1000);
  };
  
  const handleUploadFiles = async (files, fileUrls) => {
    setLoadingUpload(true);
    await uploadFiles(files, fileUrls);
  };

  const uploadFiles = async (files, fileUrls) => {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('property_id', propertyId);

      const response = await handleTokenAndPostRequest(`${window.api_host}/upload-images`, formData, {
        'Content-Type': 'multipart/form-data',
      });

      const uploadedUrls = response.data.uploaded_urls;

      // Replace local URLs with server URLs
      setUploadedImages((prevImages) => {
        const newImages = [...prevImages];
        fileUrls.forEach((fileUrl, index) => {
          const fileUrlIndex = newImages.indexOf(fileUrl);
          if (fileUrlIndex !== -1) {
            newImages[fileUrlIndex] = uploadedUrls[index];
          }
        });
        return newImages;
      });

      // Update the local to server map
      setLocalToServerMap((prevMap) => {
        const newMap = { ...prevMap };
        fileUrls.forEach((fileUrl, index) => {
          newMap[fileUrl] = uploadedUrls[index];
        });
        return newMap;
      });
      setLoadingUpload(false);

      await onUploadSuccess(uploadedUrls);
    } catch (error) {
      console.error('Error uploading images:', error);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleDeleteImage = (url) => {
    // Determine if the URL is a local or server URL
    const serverUrl = localToServerMap[url] || url;

    // Remove image immediately
    setUploadedImages((prevImages) => prevImages.filter((image) => image !== url && image !== serverUrl));

    // Remove from local to server map
    setLocalToServerMap((prevMap) => {
      const newMap = { ...prevMap };
      delete newMap[url];
      return newMap;
    });

    // Call delete image API in the background
    handleTokenAndPostRequest(`${window.api_host}/delete-image`, { url: serverUrl, property_id: propertyId })
      .then(() => onDeleteSuccess(serverUrl))
      .catch((error) => console.error('Error deleting image:', error));
  };

  const renderThumbnails = () => {
    const maxVisibleImages = 2;
    const visibleImages = uploadedImages.slice(-maxVisibleImages);
    const extraImagesCount = Math.max(uploadedImages.length - maxVisibleImages, 0);

    return (
      <div className="d-flex flex-wrap" style={{ gap: 6, marginTop: 8 }}>
        {visibleImages.map((url, index) => (
          <Image
            key={index}
            src={url}
            style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 10, border: '1px solid #f0f0f0' }}
          />
        ))}
        {extraImagesCount > 0 && (
          <div style={{ width: 80, height: 60, borderRadius: 10, backgroundColor: '#f9fafb', border: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', color: '#9ca3af', fontWeight: 600 }}>
            +{extraImagesCount} more
          </div>
        )}
      </div>
    );
  };

  const renderFullImageList = () => (
    <Container fluid>
      <Row className="d-flex flex-wrap">
        {uploadedImages.map((url, index) => (
          <Col
            key={index}
            xs={4}
            sm={3}
            md={2}
            style={{
              padding: '5px',
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            <Image
              src={url}
              thumbnail
              style={{ width: '100%', height: '70px', objectFit: 'cover' }}
            />
            {!loadingUpload && (
            <FaTimes
            onClick={() => handleDeleteImage(url)}
            style={{
                position: 'absolute',
                top: '0px',
                right: '0px',
                cursor: 'pointer',
                color: '#ef4444',
                backgroundColor: 'white',
                border: '1px solid #f0f0f0',
                borderRadius: '50%',
                padding: '3px',
                fontSize: '1.3em',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
            />
            )}
          </Col>
        ))}
      </Row>
    </Container>
  );

  const { accentColor, buttonTextColor: btnTextColor, isLight, iconColor, iconBgColor } = computeBranding(agentDetails) || {};

  return (
    <>
      <button
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          backgroundColor: '#ffffff',
          color: '#374151',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          padding: '8px 14px',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: analysisLoading ? 'not-allowed' : 'pointer',
          opacity: analysisLoading ? 0.5 : 1,
          transition: 'all 0.15s',
          boxShadow: 'none',
        }}
        disabled={analysisLoading}
        onClick={handleButtonClick}
      >
        <FaImages style={{ fontSize: '0.9rem', color: '#6b7280' }} />
        Upload & Edit Images
      </button>

      {renderThumbnails()}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        style={{ zIndex: 9999 }}
        fullscreen="sm-down"
      >
        <Modal.Body
          style={{
            overflowY: 'auto',
            padding: '1.5rem',
            ...(window.innerWidth >= 576 && { maxHeight: 'calc(100vh - 150px)' }),
          }}
        >
            {uploadedImages.length === 0 && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                <div className="text-center" style={{ color: '#9ca3af', fontSize: '0.88rem', padding: '2rem 0' }}>
                  <FaImages style={{ fontSize: '2rem', marginBottom: 12, opacity: 0.4 }} />
                  <div>Upload images to improve valuation accuracy.</div>
                </div>
            </div>
            )}
          {renderFullImageList()}
        </Modal.Body>
        <Modal.Footer style={{ position: 'sticky', bottom: 0, backgroundColor: 'white', width: '100%', borderTop: '1px solid #f0f0f0', padding: '12px 1.5rem' }}>
          <div className="d-flex w-100" style={{ gap: 10 }}>
            <Button
              style={{
                backgroundColor: `${iconBgColor}14`,
                color: iconColor,
                border: 'none',
                borderRadius: 12,
                fontWeight: 600,
                fontSize: '0.85rem',
                flex: 1,
                padding: '10px 0',
              }}
              onClick={handleFileSelect}
              disabled={loadingUpload}
            >
              {loadingUpload ? (
                <>
                  <Spinner animation="border" size="sm" /> Uploading
                </>
              ) : (
                <>
                  <FaImages style={{ marginRight: 6 }} />
                  Select Images
                </>
              )}
            </Button>
            <Button
              style={{
                backgroundColor: accentColor,
                color: btnTextColor,
                border: 'none',
                borderRadius: 12,
                fontWeight: 600,
                fontSize: '0.85rem',
                flex: 1,
                padding: '10px 0',
              }}
              onClick={handleCloseModal}
            >
              <FaCheck style={{ marginRight: 6 }} />
              Done
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ImageUploadButton;